/**
 * `useAuth()` — the session: who's signed in, sign-in/sign-out, loading state.
 *
 * The **server** decides both facts this holds (PIVOT-080). On mount it asks
 * `GET /api/auth/session` who the stored token belongs to and whether they have
 * finished onboarding, rather than decoding the token's claims and trusting a
 * cached flag: a token can be expired or signed with another environment's
 * secret, and `profile_complete` changes without the token ever being reissued.
 * The stored JWT is still read locally for one thing only — skipping a request
 * we already know is doomed.
 *
 * `sessionExpired` is set when the API rejects the token mid-session
 * (`api/client.ts::onUnauthorized`), so the app can say so once and land the
 * user back on the landing page instead of failing screen by screen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, ApiError } from '../api'
import type { SessionUser } from '../api'
import { onUnauthorized } from '../api/client'
import { useI18n } from '../i18n'
import type { Locale } from '../types/domain'
import { clearToken, decodeToken, getToken, isExpired, setToken } from './session'

interface AuthContextValue {
  user: SessionUser | null
  loading: boolean
  error: string | null
  /** The API rejected our token mid-session; the user has to sign in again. */
  sessionExpired: boolean
  signInWithGoogleCode: (code: string) => Promise<void>
  signupWithPassword: (email: string, password: string, locale: Locale) => Promise<void>
  /** Resolves with the signed-in user, so the caller can route on
   * `profile_complete` without waiting for a re-render. */
  loginWithPassword: (email: string, password: string) => Promise<SessionUser>
  signOut: () => void
  /** Re-read the session from the server (after onboarding finishes). */
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Whether a stored token is worth sending at all — saves one doomed request. */
function hasUsableToken(): boolean {
  const token = getToken()
  if (!token) return false
  const claims = decodeToken(token)
  return claims !== null && !isExpired(claims)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const { setLocale, t } = useI18n()

  const refreshSession = useCallback(async () => {
    if (!hasUsableToken()) {
      clearToken()
      setUser(null)
      return
    }
    try {
      const session = await api.auth.getSession()
      setUser(session)
      setLocale(session.locale)
    } catch (err) {
      // A 401 has already cleared the token via `onUnauthorized`; anything else
      // (the backend is down) leaves the token alone so a reload can recover.
      if (err instanceof ApiError && err.status === 401) setUser(null)
      else throw err
    }
  }, [setLocale])

  // Ask the server who we are, once, on mount. `loading` covers the request so
  // route guards don't flash a redirect before the answer arrives.
  useEffect(() => {
    void refreshSession()
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
    // Only ever run once: `setLocale` intentionally is not re-applied on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // One subscription for the whole app: any 401 from any call ends the session.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null)
        setSessionExpired(true)
      }),
    [],
  )

  const signInWithGoogleCode = useCallback(
    async (code: string) => {
      setError(null)
      try {
        const res = await api.auth.signInWithGoogleCode(code)
        setToken(res.access_token)
        setSessionExpired(false)
        setUser(res.user)
        setLocale(res.user.locale)
      } catch (err) {
        // Only the no-response case (offline, DNS, CORS) is ours to word; an
        // `ApiError` already carries the backend's message.
        setError(err instanceof ApiError ? err.message : t('auth.genericError'))
        throw err
      }
    },
    [setLocale, t],
  )

  const signupWithPassword = useCallback(
    async (email: string, password: string, locale: Locale) => {
      setError(null)
      try {
        const res = await api.auth.signup(email, password, locale)
        setToken(res.access_token)
        setSessionExpired(false)
        setUser(res.user)
        setLocale(res.user.locale)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('auth.genericError'))
        throw err
      }
    },
    [setLocale, t],
  )

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<SessionUser> => {
      setError(null)
      try {
        const res = await api.auth.login(email, password)
        setToken(res.access_token)
        setSessionExpired(false)
        setUser(res.user)
        setLocale(res.user.locale)
        return res.user
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('auth.genericError'))
        throw err
      }
    },
    [setLocale, t],
  )

  const signOut = useCallback(() => {
    clearToken()
    setUser(null)
    setSessionExpired(false)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      sessionExpired,
      signInWithGoogleCode,
      signupWithPassword,
      loginWithPassword,
      signOut,
      refreshSession,
    }),
    [
      user,
      loading,
      error,
      sessionExpired,
      signInWithGoogleCode,
      signupWithPassword,
      loginWithPassword,
      signOut,
      refreshSession,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- hook, not a component
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
